import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { ActionContext } from "vuex/types";
import { SocketHandlerEvents } from "@/services/Socket";
import {
  IObjectString,
  IChannel,
  ResponseGenericType
} from "@/store/types/types";
import { RootState } from "@/store/types/root";
import { checkDataExists } from "@/utils/dataManipulation";

const state = {
  channelKey: uuidv4(),
  channel: new BroadcastChannel("socket"),
  slaves: [] as string[],
  localData: {} as { [key: string]: any }
};

type State = typeof state;

const mutations = {
  setNewSlave: (state: State, channel: IChannel) => {
    const index = state.slaves.indexOf(channel.key);
    index === -1 && state.slaves.push(channel.key);
  },
  removeSlave: (state: State, channel: IChannel) => {
    const index = state.slaves.indexOf(channel.key);
    index >= 0 && state.slaves.splice(index, 1);
  },
  clearSlave: (state: State) => {
    state.slaves = [];
  },
  setLocalData: (
    state: State,
    data: { requestName: string; response: any }
  ) => {
    state.localData[data.requestName] = data.response;
  }
};

const actions = {
  fetchBroadcast: (
    _: ActionContext<State, RootState>,
    { message, timeout }: { message: string; timeout: number }
  ) =>
    new Promise((resolve, reject) => {
      const channel = new BroadcastChannel("socket");
      timeout = timeout || 200;

      function handleMessage(eventResponse: MessageEvent) {
        if (eventResponse.data) {
          const response =
            eventResponse.data.response ||
            typeof eventResponse.data === "object"
              ? JSON.parse(eventResponse.data)
              : eventResponse.data;
          resolve(response);
          channel.close();
        }
      }

      channel.onmessage = handleMessage;

      channel.postMessage(JSON.stringify(message));

      const timer = setTimeout(() => {
        resolve({ error: `Timeout: ${timeout}ms` });
        channel.close();
      }, timeout);

      Promise.race([channel, timer]).catch(error => {
        reject(error);
      });
    }),
  configBroadcastListener: ({
    state,
    getters
  }: ActionContext<State, RootState>) => {
    setTimeout(function() {
      if (getters.getConnect)
        state.channel.postMessage(
          JSON.stringify({
            key: state.channelKey,
            service: "ping",
            master: getters.getConnect
          })
        );
    }, 5e3);
    window.addEventListener("beforeunload", function() {
      state.channel.postMessage(
        JSON.stringify({
          service: "close",
          key: state.channelKey,
          channelKey: state.slaves[0],
          master: getters.getConnect
        })
      );
    });
  },
  handleBroadcastActions: async (
    { state, getters, commit, dispatch }: ActionContext<State, RootState>,
    {
      eventStringify,
      connectToSocket
    }: { eventStringify: MessageEvent; connectToSocket: Function }
  ) => {
    const event = JSON.parse(eventStringify.data);

    switch (event.service) {
      case "ping":
        if (!getters.getConnect)
          state.channel.postMessage(
            JSON.stringify({
              key: state.channelKey,
              service: "check_socket",
              fromMaster: getters.getConnect
            })
          );
        break;

      case "get_master":
        if (getters.getConnect)
          state.channel.postMessage(
            JSON.stringify({
              key: state.channelKey,
              service: "response_master"
            })
          );
        break;

      case "check_socket":
        commit("setNewSlave", event);
        break;

      case "changeProfile":
        axios.defaults.headers.common["Authorization"] = event.token;
        commit("loginUser", event);
        break;

      case "broadcast":
        if (!getters.getConnect)
          SocketHandlerEvents("SOCKET_BROADCAST", eventStringify);
        break;

      case "broadcast_local":
        SocketHandlerEvents("SOCKET_BROADCAST", eventStringify);
        break;

      case "close":
        if (await dispatch("holidaysRequest")) dispatch("authLogout");
        commit("removeSlave", event);
        if (event.fromMaster && event.channelKey === state.channelKey)
          await connectToSocket();
        break;
    }
  },
  checkExistOpenSocket: (
    { state, getters, dispatch }: ActionContext<State, RootState>,
    {
      connectToSocket,
      masterSocketExists
    }: { connectToSocket: Function; masterSocketExists: boolean }
  ) => {
    dispatch("configBroadcastListener");
    if (masterSocketExists) {
      state.channel.postMessage(
        JSON.stringify({
          key: state.channelKey,
          service: "check_socket",
          fromMaster: getters.getConnect
        })
      );
    }
    state.channel.onmessage = (eventStringify: MessageEvent) => {
      const actionName = eventStringify.data?.id
        ? "localFetch"
        : "handleBroadcastActions";
      dispatch(actionName, { eventStringify, connectToSocket });
    };
  },
  localFetch: (
    { state, getters }: ActionContext<State, RootState>,
    { eventStringify }: { eventStringify: MessageEvent }
  ) => {
    const event = eventStringify.data;
    if (
      event.requestName &&
      checkDataExists(getters.getLocalData(event.requestName))
    ) {
      state.channel.postMessage({
        id: event.id,
        status: "ok",
        type: "response",
        response: getters.getLocalData(event.requestName),
        duration: Date.now() - event.timeStart
      });
    }
  },
  customFetch: (
    { state }: ActionContext<State, RootState>,
    { requestName, options }: { requestName: string; options?: any }
  ) => {
    const timeout = 200;
    return new Promise((resolve, reject) => {
      const requestId = uuidv4();
      let timeoutId: ReturnType<typeof setTimeout>; // eslint-disable-line prefer-const

      function responseHandler(event: { data: any }) {
        const { id, error } = event.data;
        if (id === requestId) {
          state.channel.removeEventListener("message", responseHandler);
          clearTimeout(timeoutId);

          if (error) {
            reject(error);
          } else {
            resolve(event.data);
          }
        }
      }

      state.channel.addEventListener("message", responseHandler);

      timeoutId = setTimeout(() => {
        state.channel.removeEventListener("message", responseHandler);
        reject(new Error("Request timed out"));
      }, timeout);

      state.channel.postMessage({
        id: requestId,
        requestName,
        options,
        timeStart: Date.now()
      });
    });
  },
  sendAllChannels: (
    { state }: ActionContext<State, RootState>,
    event: IObjectString
  ) => state.channel.postMessage(JSON.stringify(event)),
  sendAllSlaves: (
    { state, getters }: ActionContext<State, RootState>,
    event: IObjectString
  ) => {
    if (getters.getConnect)
      state.channel.postMessage(
        JSON.stringify({ ...event, service: "broadcast" })
      );
  },
  checkLocalData: async (
    { dispatch, commit }: ActionContext<State, RootState>,
    { requestName, request }: { requestName: string; request: Function }
  ) => {
    return await dispatch("customFetch", { requestName })
      .then(({ response }) => {
        commit("setLocalData", { requestName, response });
        return response;
      })
      .catch(async () => {
        const results: ResponseGenericType = await request();
        if (results.kind === "ok" && results.data)
          commit("setLocalData", { requestName, response: results });
        return results;
      });
  }
};

const getters = {
  getChannelKey: (state: State) => state.channelKey,
  getLocalData: (state: State) => (keyName: string) => state.localData[keyName]
};

export default {
  state,
  mutations,
  actions,
  getters
};
